import './NuiBet.less';

import React, { FC, useContext } from 'react';
import clsx from 'clsx';
import { ErrorBoundary } from '../../common/ErrorBoundary';
import BetLocationContext, { BetLocactionSectionOptions } from '../../common/BetLocationContext';
import { useBetslipPick } from '../betslip/hooks';
import { LocalizedBetForAnalysisResponse, Selection } from '../../types/generated/nikeone';
import { Pick } from '../../types/generated/nike-forum';
import { getSingleSelection, isSelectionPopular, validState } from './utils';
import { isMobileChannel } from '../../common/util';
import { LinkToPrematchDetail } from '../../common/utils/utils';
import { useFormattedOdds } from './hooks/useFormattedOdds';
import { SvgIcon } from '../../icons/SvgIcon';
import { BetSelectionLocked } from '../turboLive/components/BetSelection';
import i18n from '../../common/i18n';
import BetbookContext from '../../common/BetbookContext';

// unifying property names header - market, sportEventId - matchId
type UnifiedBetData<T> = T extends LocalizedBetForAnalysisResponse
    ? T
    : T extends Pick
    ? Omit<T, 'market' | 'matchId'> & { header: T['market']; sportEventId: T['matchId'] }
    : never;

interface NuiBetType {
    betData: UnifiedBetData<LocalizedBetForAnalysisResponse & Pick>; // useBetsData data or pick from BB
    isLoading: boolean;
    selectionCode?: number; // visible only one selection (with this code) from selection grid (BB overview)
    editorTipCode?: number;
    editorTicketReturned?: boolean;
    prevOdds?: number; // prevOdds of selectionCode or editorTipCode
    className?: string;
    betLocationSection: BetLocactionSectionOptions;
    showBetCount?: boolean;
}

const NuiBet: FC<NuiBetType> = ({ betData, ...props }) => (
    <ErrorBoundary description="NuiBet">
        <BetLocationContext.Provider value={{ betLocationSection: props.betLocationSection }}>
            {validState(betData?.state) ? <NuiBetView betData={betData} {...props} /> : <NuiBetHidden />}
        </BetLocationContext.Provider>
    </ErrorBoundary>
);

const NuiBetView: FC<NuiBetType> = ({
    betData,
    isLoading,
    selectionCode,
    editorTipCode,
    editorTicketReturned = false,
    prevOdds,
    className,
    showBetCount = false
}) => {
    // TODO: add dummy maybe?
    if (isLoading) return null;

    const betId = betData?.betId;
    const matchBetCount = betData && 'matchBetCount' in betData ? betData.matchBetCount : 0;
    const singleSelectionLayout = !!selectionCode;
    const selectionGrid = singleSelectionLayout
        ? getSingleSelection(betData?.selectionGrid, `${selectionCode}`)
        : betData?.selectionGrid;
    const labelResolved = betData?.headerDetail || (betData as Pick)?.market;
    return selectionGrid ? (
        <div
            className={clsx(className, 'flex flex-col', {
                'nui-bet-base': selectionCode
            })}
        >
            <NuiBetHeader
                label={labelResolved}
                matchId={`${betData.sportEventId}`}
                betCount={matchBetCount}
                showBetCount={showBetCount && !!matchBetCount}
            />
            <div className="nui-bet br-radius-2 flex flex-col">
                {singleSelectionLayout ? (
                    <NuiBetRow
                        key={betId}
                        betId={`${betId}`}
                        selectionsRow={selectionGrid as Selection[]}
                        selectionCode={selectionCode}
                        editorTipCode={editorTipCode}
                        editorTicketReturned={editorTicketReturned}
                        prevOdds={prevOdds}
                    />
                ) : (
                    <>
                        {selectionGrid.map((selectionsRow, index) => (
                            <NuiBetRow
                                key={index}
                                betId={`${betId}`}
                                selectionsRow={selectionsRow}
                                editorTipCode={editorTipCode}
                                editorTicketReturned={editorTicketReturned}
                                prevOdds={prevOdds}
                            />
                        ))}
                    </>
                )}
            </div>
        </div>
    ) : null;
};

const NuiBetRow: FC<{
    betId: string;
    selectionsRow: Selection[];
    selectionCode?: number;
    editorTipCode?: number;
    editorTicketReturned?: boolean;
    prevOdds?: number;
}> = ({ betId, selectionsRow, selectionCode, editorTipCode, editorTicketReturned, prevOdds }) => (
    <div className="nui-bet-row flex">
        {selectionsRow.map((selection: Selection) => {
            const isLabel = selection?.type === 'label';
            return isLabel ? (
                <NuiBetLabel key={betId} label={selection.name} bold={selection.bold} />
            ) : (
                <NuiBetSelection
                    key={selection.tip}
                    betId={betId}
                    selection={selection}
                    selectionCode={selectionCode}
                    editorTipCode={editorTipCode}
                    showReturnedIcon={editorTicketReturned && selection.tip === `${editorTipCode}`}
                    prevOdds={prevOdds}
                />
            );
        })}
    </div>
);

const NuiBetHeader: FC<{ label: string; matchId: string; betCount: number; showBetCount: boolean }> = ({
    label,
    matchId,
    betCount,
    showBetCount
}) => (
    <div className="nui-bet-header py-5 flex justify-between fs-14">
        <span className="c-black-50 ellipsis text-bold">{label}</span>
        {showBetCount && (
            <LinkToPrematchDetail matchId={matchId}>
                <button className={clsx('p-0 c-blue', isMobileChannel() ? 'text-extra-bold' : 'text-bold')}>
                    {(betCount >= 0 ? '+' : '-') + betCount}
                </button>
            </LinkToPrematchDetail>
        )}
    </div>
);

const NuiBetSelection: FC<{
    betId: string;
    selection: Selection;
    selectionCode?: number;
    editorTipCode?: number;
    showReturnedIcon?: boolean;
    prevOdds?: number;
}> = ({ betId, selection, selectionCode, editorTipCode, showReturnedIcon, prevOdds }) => {
    const { betLocationSection } = useContext(BetLocationContext);
    const { formattedOdds, formattedPrevOdds } = useFormattedOdds(selection?.odds, prevOdds);
    const locked = selection.locked;
    const winState = selection.winState?.toLowerCase();
    const editorTip = `${editorTipCode}` === selection.tip;
    const highlighted = !winState && editorTip;
    const showPrevOdds = editorTip || `${selectionCode}` === selection.tip;
    const arrowUp = showPrevOdds && prevOdds < selection.odds;
    const arrowDown = showPrevOdds && prevOdds > selection.odds;
    const singleSelectionLayout = !!selectionCode;
    const isPopular = !singleSelectionLayout && isSelectionPopular(selection);

    const addPickProperties = {
        betId,
        selectionCode: selection.tip,
        odds: selection.odds,
        // @ts-ignore
        positionId: betLocationSection?.id,
        // @ts-ignore
        positionCode: betLocationSection?.code
    };
    const { betbookPostId, betbookBetSelectionCode, betbookPostAuthor } = useContext(BetbookContext);
    if (betbookPostId && String(selection.tip) === String(betbookBetSelectionCode)) {
        // @ts-ignore
        addPickProperties.betbookId = betbookPostId;
        // @ts-ignore
        addPickProperties.betbookContractId = betbookPostAuthor;
    }

    const { isSelected, addPick, removePick } = useBetslipPick(addPickProperties);

    const notSelectedWin = !!winState && !isSelected;
    const isDisabled = (locked || showReturnedIcon || !selection.enabled) && !isSelected;
    const isBtnDisabled = notSelectedWin || isDisabled;

    const onClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => (isSelected ? removePick(e) : addPick(e));

    // green color - always
    // red color - only in single selection layout || editorTip
    const getWinState = (winState: string) =>
        singleSelectionLayout || winState === 'win' || editorTip ? winState : false;

    return (
        <button
            className={clsx('nui-bet-cell flex-center px-10', {
                [`nui-bet-${getWinState(winState)}${editorTip ? '-editor' : ''}`]:
                    getWinState(winState) && !isSelected && !showReturnedIcon,
                'nui-bet-editor': highlighted && !isSelected && !showReturnedIcon && !singleSelectionLayout,
                'nui-bet-selected': isSelected,
                'nui-bet-returned-editor': showReturnedIcon && editorTip,
                'nui-bet-disabled': (!selection.enabled || showReturnedIcon || locked) && !getWinState(winState),
                'nui-single-sel': singleSelectionLayout
            })}
            onClick={e => onClick(e)}
            disabled={isBtnDisabled}
            data-atid="n1-bet-box"
        >
            <span className={clsx('flex-1 pr-5 text-left fs-12 ellipsis')}>{selection.name}</span>
            {(arrowUp || arrowDown) && !isDisabled && formattedPrevOdds && (
                <span className={clsx('flex-center fs-14 lh-1')}>
                    {formattedPrevOdds}
                    <SvgIcon
                        imgId="bet-arrow"
                        sizeX="10"
                        sizeY="10"
                        className={clsx('mx-10', arrowUp ? 'c-green' : 'c-red nui-bet-down')}
                    />
                </span>
            )}
            {showReturnedIcon && <SvgIcon imgId="reload" sizeX="10" sizeY="10" className={clsx('mx-10')} />}
            {locked ? (
                <BetSelectionLocked />
            ) : (
                formattedOdds && (
                    <span
                        title={isPopular ? i18n('react.bet.mostbet') : undefined}
                        className={clsx(
                            'fs-14 text-extra-bold  lh-1',
                            isPopular && !isBtnDisabled ? 'marked-bet' : 'nui-bet-odds'
                        )}
                    >
                        {formattedOdds}
                    </span>
                )
            )}
        </button>
    );
};

const NuiBetLabel: FC<{ label: string; bold?: boolean }> = ({ label, bold }) => (
    <div
        className={clsx('nui-bet-label flex items-center fs-12 ellipsis c-black-100 px-10', {
            'text-extra-bold': bold
        })}
    >
        <span className="ellipsis">{label}</span>
    </div>
);

const NuiBetHidden: FC = () => (
    <div className="fs-14 text-bold my-30 text-center c-default-light">{i18n('nuibet.hidden')}</div>
);

export default NuiBet;
